import React, { useState, useEffect } from 'react';
import { ForecastCommentary, User } from '@rti/shared/src/types';
import { forecastAPI } from '@/services/commentary.service';
import RichTextEditor from '@/components/RichTextEditor';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from "@rti/shared/";

interface ForecastFormProps {
  onSave?: (data: ForecastCommentary) => void;
  onCancel?: () => void;
  initialData?: Partial<ForecastCommentary>;
}

export const ForecastForm: React.FC<ForecastFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const { user } = useAuth();

  const getCurrentQuarter = () => {
    const month = new Date().getMonth() + 1;
    return Math.ceil(month / 3);
  };

  const getCurrentYear = () => new Date().getFullYear();

  const getQuarterStartDate = (year: number, quarter: number): string => {
    const monthMap: Record<number, string> = {
      1: '01-01',
      2: '04-01',
      3: '07-01',
      4: '10-01',
    };
    return `${year}-${monthMap[quarter]}`;
  };

  const parseInitialData = (data?: Partial<ForecastCommentary>) => {
    if (data?.forecastQtrDateId) {
      const [year] = data.forecastQtrDateId.split('-');
      return {
        year: parseInt(year),
        quarter: data.forecastQtr || 1,
        polymer: data.polymer || '',
        commentary: data.commentary || '',
      };
    }

    if (data?.forecastYear && data?.forecastQtr) {
      return {
        year: data.forecastYear,
        quarter: data.forecastQtr,
        polymer: data.polymer || '',
        commentary: data.commentary || '',
      };
    }

    return {
      year: getCurrentYear(),
      quarter: getCurrentQuarter(),
      polymer: '',
      commentary: '',
    };
  };

  const initial = parseInitialData(initialData);
  const currentUserName = user ? `${user.first_name} ${user.last_name}` : '';
  const [selectedYear, setSelectedYear] = useState(initial.year);
  const [selectedQuarter, setSelectedQuarter] = useState(initial.quarter);
  const [formData, setFormData] = useState<Partial<ForecastCommentary>>({
    forecastQtrDateId: initialData?.forecastQtrDateId || getQuarterStartDate(initial.year, initial.quarter),
    forecastQtr: initialData?.forecastQtr || initial.quarter,
    forecastYear: initial.year,
    commentary: initialData?.commentary || initial.commentary,
    polymer: initialData?.polymer || initial.polymer,
    lastUpdatedBy: initialData?.lastUpdatedBy || currentUserName,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [currentRecordPendingChange, setCurrentRecordPendingChange] = useState<ForecastCommentary | null>(null);
  const [originalCommentary, setOriginalCommentary] = useState<string>('');

  const polymers = ['PE', 'PP', 'PS', 'PVC', 'PET'];

  useEffect(() => {
    if (initialData) {
      const parsed = parseInitialData(initialData);
      setSelectedYear(parsed.year);
      setSelectedQuarter(parsed.quarter);

      const checkForPendingChange = async () => {
        try {
          const pending = await forecastAPI.getPending();
          const matchingPending = pending.find(p => {
            const pendingYear = p.forecastQtrDateId ? parseInt(p.forecastQtrDateId.split('-')[0]) : null;
            return pendingYear === parsed.year &&
                   p.forecastQtr === parsed.quarter &&
                   p.polymer === parsed.polymer;
          });

          if (matchingPending) {
            setCurrentRecordPendingChange(matchingPending);
            setOriginalCommentary(initialData.commentary || '');
            setFormData({
              forecastQtrDateId: initialData.forecastQtrDateId || getQuarterStartDate(parsed.year, parsed.quarter),
              forecastQtr: parsed.quarter,
              forecastYear: parsed.year,
              commentary: matchingPending.commentary || '',
              polymer: initialData.polymer || '',
              lastUpdatedBy: matchingPending.lastUpdatedBy || currentUserName,
            });
          } else {
            setCurrentRecordPendingChange(null);
            setOriginalCommentary('');
            setFormData({
              forecastQtrDateId: initialData.forecastQtrDateId || getQuarterStartDate(parsed.year, parsed.quarter),
              forecastQtr: parsed.quarter,
              forecastYear: parsed.year,
              commentary: initialData.commentary || '',
              polymer: initialData.polymer || '',
              lastUpdatedBy: initialData.lastUpdatedBy || currentUserName,
            });
          }
        } catch (error) {
          console.error('Error checking pending changes:', error);
        }
      };

      checkForPendingChange();
    }
  }, [initialData, currentUserName]);

  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        const admins = await apiClient.getAdminUsers();
        setAdminUsers(admins);
      } catch (error) {
        console.error('Error fetching admin users:', error);
      }
    };
    fetchAdminUsers();
  }, []);

  const generateYearOptions = () => {
    const currentYear = getCurrentYear();
    const years: number[] = [];

    for (let i = 0; i <= 3; i++) {
      years.push(currentYear + i);
    }

    return years;
  };

  const getAvailableQuarters = () => {
    return [1, 2, 3, 4];
  };

  const isPastQuarter = (year: number, quarter: number): boolean => {
    const currentYear = getCurrentYear();
    const currentQuarter = getCurrentQuarter();

    if (year < currentYear) return true;
    if (year === currentYear && quarter < currentQuarter) return true;
    return false;
  };

  useEffect(() => {
    const forecastQtrDateId = getQuarterStartDate(selectedYear, selectedQuarter);
    setFormData((prev) => ({
      ...prev,
      forecastQtrDateId,
      forecastQtr: selectedQuarter,
      forecastYear: selectedYear,
    }));
  }, [selectedYear, selectedQuarter]);


  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
  };

  const handleQuarterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedQuarter(parseInt(e.target.value));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const hasFieldChanged = (fieldName: keyof ForecastCommentary): boolean => {
    if (!currentRecordPendingChange || !originalCommentary) {
      return false;
    }
    if (fieldName === 'commentary') {
      return formData.commentary !== originalCommentary;
    }
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (
      !formData.forecastQtrDateId ||
      !formData.forecastQtr ||
      !formData.commentary ||
      !formData.polymer
    ) {
      setError('All fields are required');
      return;
    }

    if (!initialData && isPastQuarter(selectedYear, selectedQuarter)) {
      setError('Cannot create forecast for past quarters. Please select current or future quarter.');
      return;
    }

    setLoading(true);
    try {
      const dataToSave: ForecastCommentary = {
        ...formData,
        lastUpdatedBy: formData.lastUpdatedBy || currentUserName,
        lastUpdatedTime: new Date().toISOString(),
      } as ForecastCommentary;

      await forecastAPI.create(dataToSave);
      setSuccess(true);
      if (onSave) onSave(dataToSave);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    const currentYear = getCurrentYear();
    const currentQuarter = getCurrentQuarter();
    setSelectedYear(currentYear);
    setSelectedQuarter(currentQuarter);
    setFormData({
      forecastQtrDateId: getQuarterStartDate(currentYear, currentQuarter),
      forecastQtr: currentQuarter,
      forecastYear: currentYear,
      commentary: '',
      polymer: '',
      lastUpdatedBy: currentUserName,
    });
    setError(null);
    setSuccess(false);
  };

  return (
    <div className="forecast-form bg-rs-black text-rs-off-white p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-6 text-rs-green">
        {initialData ? 'Edit' : 'New'} Forecast Commentary
      </h2>

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">
          Data saved successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">
              Quarter *
            </label>
            <select
              value={selectedQuarter}
              onChange={handleQuarterChange}
              className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
              required
            >
              {getAvailableQuarters().map((quarter) => (
                <option key={quarter} value={quarter}>
                  Q{quarter}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">
              Year *
            </label>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
              required
            >
              {generateYearOptions().map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-2">
              Polymer *
            </label>
            <select
              name="polymer"
              value={formData.polymer || ''}
              onChange={handleChange}
              className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
              required
            >
              <option value="">Select Polymer</option>
              {polymers.map((polymer) => (
                <option key={polymer} value={polymer}>
                  {polymer}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Last Updated By *
          </label>
          <select
            name="lastUpdatedBy"
            value={formData.lastUpdatedBy || ''}
            onChange={handleChange}
            className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
            required
          >
            <option value="">Select User</option>
            {adminUsers.map((admin) => (
              <option key={admin.user_id} value={`${admin.first_name} ${admin.last_name}`}>
                {admin.first_name} {admin.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-rs-jet border border-rs-jet-light rounded px-4 py-3">
          <p className="text-sm text-rs-off-white/80">
            <span className="font-medium">Forecast Quarter Date ID:</span>{' '}
            <span className="text-rs-green">{formData.forecastQtrDateId}</span>
          </p>
          <p className="text-xs text-rs-off-white/60 mt-1">
            Auto-generated from selected year and quarter
          </p>
        </div>

        {currentRecordPendingChange && originalCommentary && (
          <div className="bg-rs-jet border border-rs-jet-light rounded px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-rs-off-white">Current Production Commentary:</span>
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Read-Only</span>
            </div>
            <div
              className="text-sm text-rs-off-white/80 p-3 bg-rs-black rounded border border-rs-jet-light"
              dangerouslySetInnerHTML={{ __html: originalCommentary }}
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              Commentary (max 4000 chars) *
            </label>
            {currentRecordPendingChange && (
              <span className="px-2 py-0.5 bg-rs-salmon/20 text-rs-salmon text-xs rounded flex items-center gap-1">
                <span className="w-2 h-2 bg-rs-salmon rounded-full animate-pulse"></span>
                Pending Change
              </span>
            )}
          </div>
          <div className={hasFieldChanged('commentary') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.commentary || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, commentary: value }))}
              placeholder={currentRecordPendingChange ? "This is your pending change..." : "Enter forecast commentary..."}
              rows={10}
            />
          </div>
          <p className="text-xs text-rs-off-white/60 mt-1">
            {formData.commentary?.replace(/<[^>]*>/g, '').length || 0} / 4000 characters
          </p>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-rs-green hover:bg-rs-light-green !text-rs-black font-semibold px-6 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save to Staging'}
          </button>


          <button
            type="button"
            onClick={handleClear}
            className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-6 py-2 rounded transition-colors"
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="bg-rs-jet hover:bg-rs-jet-light text-rs-off-white font-semibold px-6 py-2 rounded transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ForecastForm;

